import type { ListMemoryThreadsResponse } from '@mastra/client-js';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { FALLBACK_THREAD_TITLE, normalizeThreadTitle, type ThreadTitleUpdate } from '../lib/threadTitle';

const THREADS_QUERY_KEY = ['AI', 'THREADS'];

/** Renames the thread in every cached thread list. Returns whether any list held that thread. */
export const applyThreadTitle = (queryClient: QueryClient, { threadId, title }: ThreadTitleUpdate) => {
  const normalizedTitle = normalizeThreadTitle(title);
  if (normalizedTitle === FALLBACK_THREAD_TITLE) return false;

  let applied = false;
  queryClient.setQueriesData({ queryKey: THREADS_QUERY_KEY }, (oldData: ListMemoryThreadsResponse | undefined) => {
    if (!oldData?.threads?.some((thread) => thread.id === threadId)) return oldData;

    applied = true;
    return {
      ...oldData,
      threads: oldData.threads.map((thread) => (thread.id === threadId ? { ...thread, title: normalizedTitle } : thread)),
    };
  });
  return applied;
};

/** True while no cached list knows the thread by a real title, so a refetch may still bring it. */
export const isThreadTitlePending = (queryClient: QueryClient, threadId: string) =>
  !queryClient
    .getQueriesData<ListMemoryThreadsResponse>({ queryKey: THREADS_QUERY_KEY })
    .some(([, data]) => data?.threads?.some((thread) => thread.id === threadId && normalizeThreadTitle(thread.title) !== FALLBACK_THREAD_TITLE));

/**
 * Thread title cache for the chat stream: `applyTitle` takes the `data-thread-title` part, and
 * `isTitlePending` tells `onFinish` whether the delayed refetch fallback is still needed.
 */
export const useThreadTitleCache = () => {
  const queryClient = useQueryClient();

  return {
    applyTitle: (update: ThreadTitleUpdate) => applyThreadTitle(queryClient, update),
    isTitlePending: (threadId: string) => isThreadTitlePending(queryClient, threadId),
  };
};
